import { useEffect, useRef, useCallback } from 'react';
import { useData } from '../contexts/DataContext';

interface UseAutoRefreshOptions {
  enabled?: boolean;
  interval?: number; // in milliseconds
  onlyWhenVisible?: boolean;
}

export const useAutoRefresh = (options: UseAutoRefreshOptions = {}) => {
  const {
    enabled = true,
    interval = 30000, // 30 secondi
    onlyWhenVisible = true
  } = options;

  const { loadContenuti, state } = useData();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<number>(Date.now());

  const isDocumentVisible = useCallback(() => {
    return !onlyWhenVisible || document.visibilityState === 'visible';
  }, [onlyWhenVisible]);

  const refreshData = useCallback(async () => {
    if (!enabled || !isDocumentVisible()) {
      return;
    }

    try {
      await loadContenuti();
      lastRefreshRef.current = Date.now();
    } catch (error) {
      console.error('Auto-refresh error:', error);
    }
  }, [enabled, isDocumentVisible, loadContenuti]);

  const startAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (!enabled) {
      return;
    }

    intervalRef.current = setInterval(refreshData, interval);
  }, [enabled, interval, refreshData]);

  const stopAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const forceRefresh = useCallback(async () => {
    await refreshData();
  }, [refreshData]);

  // Gestione visibilità della pagina
  useEffect(() => {
    if (!onlyWhenVisible) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Quando la pagina diventa visibile, controlla se è necessario un refresh
        const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
        if (timeSinceLastRefresh >= interval) {
          refreshData();
        }
        startAutoRefresh();
      } else {
        stopAutoRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onlyWhenVisible, interval, refreshData, startAutoRefresh, stopAutoRefresh]);

  // Avvia/ferma l'auto-refresh
  useEffect(() => {
    if (enabled && isDocumentVisible()) {
      startAutoRefresh();
    } else {
      stopAutoRefresh();
    }

    return stopAutoRefresh;
  }, [enabled, startAutoRefresh, stopAutoRefresh, isDocumentVisible]);

  return {
    forceRefresh,
    lastRefresh: lastRefreshRef.current,
    isRefreshing: false, // Potresti implementare questo se necessario
    contenutiCount: state.contenuti.length
  };
};